import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'App/store';
import { SelectedProductInterface } from 'domains/Catalog/Products/components/SelectProductList/types';
import {
  getMoreSelectProductList,
  getSelectProductList,
} from 'domains/Catalog/Products/productsSlice';
import {
  getSelectProductListDataSelector,
  getSelectProductListPaginationSelector,
  getSelectProductListRefreshStatusSelector,
  getSelectProductListStatusSelector,
} from 'domains/Catalog/Products/productsSlice/productSelectors/selectProductListSelectors';

interface UseProductsForRestrictionsResult {
  products: SelectedProductInterface[];
  hasMoreProducts: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  fetchProducts: (searchTerm?: string) => Promise<void>;
  loadMoreProducts: (searchTerm?: string) => Promise<void>;
}

export function useProductsForRestrictions(): UseProductsForRestrictionsResult {
  const dispatch = useAppDispatch();
  const products = useSelector(getSelectProductListDataSelector);
  const pagination = useSelector(getSelectProductListPaginationSelector);
  const { isLoading } = useSelector(getSelectProductListStatusSelector);
  const { isRefreshing } = useSelector(
    getSelectProductListRefreshStatusSelector,
  );

  const hasMoreProducts = useMemo(() => {
    if (!pagination) return false;
    return pagination.currentPage < pagination.totalPages;
  }, [pagination]);

  const fetchProducts = useCallback(
    async (q?: string) => {
      await dispatch(
        getSelectProductList({
          ...(q && { q }),
        }),
      );
    },
    [dispatch],
  );

  const loadMoreProducts = useCallback(
    async (q?: string) => {
      await dispatch(
        getMoreSelectProductList({
          ...(q && { q }),
        }),
      );
    },
    [dispatch],
  );

  return {
    products: products || [],
    hasMoreProducts,
    isLoading,
    isRefreshing,
    fetchProducts,
    loadMoreProducts,
  };
}

export default useProductsForRestrictions;
